// src/admin/pages/roles/RoleManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Pagination,
  Collapse,
  Backdrop,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as RoleIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { Role, RoleFormData, APP_FEATURES, FEATURE_GROUPS, Feature } from './role';
import { useAuth } from '../../../contexts/AuthContext';
import {
  useRolesPaginated,
  useRoleStats,
  useRoleMutations,
} from '../../../hooks/useRoles';
import ToastNotification from '../common/Toastnotification';

const ROLES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// ─────────────────────────────────────────────────────────────────────────────
// Chip style helper (copied from BlogManagement)
// ─────────────────────────────────────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// View Role Dialog
// ─────────────────────────────────────────────────────────────────────────────
interface ViewRoleDialogProps {
  open: boolean;
  role: Role | null;
  onClose: () => void;
  isDark: boolean;
}

const ViewRoleDialog: React.FC<ViewRoleDialogProps> = ({ open, role, onClose, isDark }) => {
  if (!role) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RoleIcon color="primary" />
          <Typography variant="h6">Role Details</Typography>
        </Stack>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Name */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Role Name
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
              {role.name}
            </Typography>
          </Box>

          {/* Description */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Description
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
              {role.description || '—'}
            </Typography>
          </Box>

          <Divider />

          {/* Permissions grouped */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
              Permissions ({role.permissions.length} of {APP_FEATURES.length})
            </Typography>

            {role.permissions.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                No permissions assigned.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {FEATURE_GROUPS.map((group) => {
                  const groupFeatures = APP_FEATURES.filter((f) => f.group === group);
                  const granted = groupFeatures.filter((f) => role.permissions.includes(f.code));
                  if (granted.length === 0) return null;
                  return (
                    <Box key={group}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: 'primary.main',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          mb: 0.75,
                          display: 'block',
                        }}
                      >
                        {group}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.75}>
                        {granted.map((f) => (
                          <Chip
                            key={f.code}
                            label={f.name}
                            size="small"
                            icon={<LockIcon />}
                            variant="outlined"
                            sx={getSoftChipSx('success', isDark)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Dates */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Created
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {role.createdAt?.toLocaleDateString() ?? '—'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Last Updated
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {role.updatedAt?.toLocaleDateString() ?? '—'}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Permission Group Checkbox Panel
// ─────────────────────────────────────────────────────────────────────────────
interface PermissionGroupProps {
  group: string;
  features: Feature[];
  selectedCodes: string[];
  onChange: (code: string, checked: boolean) => void;
  onSelectAll: (group: string, selectAll: boolean) => void;
  isDark: boolean;
}

const PermissionGroup: React.FC<PermissionGroupProps> = ({
  group,
  features,
  selectedCodes,
  onChange,
  onSelectAll,
  isDark,
}) => {
  const selectedInGroup = features.filter((f) => selectedCodes.includes(f.code));
  const allSelected = selectedInGroup.length === features.length;
  const someSelected = selectedInGroup.length > 0 && !allSelected;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      {/* Group header with select-all */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={(e) => onSelectAll(group, e.target.checked)}
              size="small"
              color="primary"
              checkedIcon={<CheckBoxIcon />}
              indeterminateIcon={<IndeterminateCheckBoxIcon />}
              icon={<CheckBoxOutlineBlankIcon />}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {group}
            </Typography>
          }
          sx={{ m: 0 }}
        />
        <Chip
          label={`${selectedInGroup.length} / ${features.length}`}
          size="small"
          variant="outlined"
          sx={getSoftChipSx(allSelected ? 'success' : someSelected ? 'warning' : 'default', isDark)}
        />
      </Box>

      {/* Individual checkboxes */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 0,
        }}
      >
        {features.map((feature) => (
          <FormControlLabel
            key={feature.code}
            control={
              <Checkbox
                checked={selectedCodes.includes(feature.code)}
                onChange={(e) => onChange(feature.code, e.target.checked)}
                size="small"
                color="primary"
              />
            }
            label={
              <Typography variant="body2" noWrap title={feature.name}>
                {feature.name}
              </Typography>
            }
            sx={{ m: 0, py: 0.5, pr: 1 }}
          />
        ))}
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RoleManagement
// ─────────────────────────────────────────────────────────────────────────────
const RoleManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── UI state ──────────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);

  const canView = isSuperAdmin || hasPermission('roles.view');
  const canCreate = isSuperAdmin || hasPermission('roles.create');
  const canEdit = isSuperAdmin || hasPermission('roles.edit');
  const canDelete = isSuperAdmin || hasPermission('roles.delete');

  const [formData, setFormData] = useState<RoleFormData>({
    name: '',
    description: '',
    permissions: [],
  });

  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  const [viewRole, setViewRole] = useState<Role | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const formScrollRef = useRef<HTMLDivElement>(null);

  const saveAction = drawerMode === 'add' ? 'Creating Role…' : 'Updating Role…';

  // ── React Query ───────────────────────────────────────────────────────────
  const {
    data: rolesData,
    isLoading: rolesLoading,
    isFetching: rolesIsFetching,
  } = useRolesPaginated({
    page: currentPage,
    search: searchQuery,
    pageSize: ROLES_PER_PAGE,
  });

  const { data: statsData } = useRoleStats();
  const { create, update, remove } = useRoleMutations();

  const roles = rolesData?.roles ?? [];
  const totalRoles = rolesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalRoles / ROLES_PER_PAGE);
  const totalRolesCount = statsData?.total ?? 0;
  const loading = rolesLoading || rolesIsFetching;

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: ToastState['severity']) => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  // ── Pagination ────────────────────────────────────────────────────────────
  const resetToFirstPage = () => setCurrentPage(1);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Clear filters ─────────────────────────────────────────────────────────
  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '';
  const activeFiltersCount = [searchQuery !== ''].filter(Boolean).length;

  // ── Form helpers ──────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormData({ name: '', description: '', permissions: [] });
    setSelectedRole(null);
  };

  const handleAddRole = () => {
    if (!canCreate) return;
    resetForm();
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const handleEditRole = (role: Role) => {
    if (!canEdit) return;
    setFormData({
      name: role.name,
      description: role.description,
      permissions: [...role.permissions],
    });
    setSelectedRole(role);
    setDrawerMode('edit');
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    resetForm();
  };

  // ── View ──────────────────────────────────────────────────────────────────
  const handleViewRole = (role: Role) => {
    setViewRole(role);
    setViewDialogOpen(true);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteRole = (role: Role) => { if (!canDelete) return; setDeleteTarget(role); }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Role deleted successfully', 'success');
    } catch (err: any) {
      console.error('Error deleting role:', err);
      showToast(`Failed to delete role: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Permission checkbox handlers ──────────────────────────────────────────
  const handlePermissionChange = (code: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...prev.permissions, code]
        : prev.permissions.filter((c) => c !== code),
    }));
  };

  const handleSelectAllGroup = (group: string, selectAll: boolean) => {
    const groupCodes = APP_FEATURES
      .filter((f) => f.group === group)
      .map((f) => f.code);

    setFormData((prev) => {
      const withoutGroup = prev.permissions.filter((c) => !groupCodes.includes(c));
      return {
        ...prev,
        permissions: selectAll ? [...withoutGroup, ...groupCodes] : withoutGroup,
      };
    });
  };

  const handleSelectAll = (selectAll: boolean) => {
    setFormData((prev) => ({
      ...prev,
      permissions: selectAll ? APP_FEATURES.map((f) => f.code) : [],
    }));
  };

  const allSelected = formData.permissions.length === APP_FEATURES.length;
  const someSelected = formData.permissions.length > 0 && !allSelected;

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      showToast('Role name is required', 'error');
      return;
    }
    if (!formData.description.trim()) {
      showToast('Description is required', 'error');
      return;
    }
    if (formData.permissions.length === 0) {
      showToast('Please select at least one permission', 'warning');
      return;
    }

    try {
      setSaveLoading(true);

      // Check for duplicate name
      //   const nameTaken = await import('../../../admin/pages/roles/roleService').then((m) =>
      //     m.RoleService.isNameTaken(formData.name, selectedRole?.id)
      //   );
      //   if (nameTaken) {
      //     showToast('A role with this name already exists', 'error');
      //     setSaveLoading(false);
      //     return;
      //   }

      if (drawerMode === 'add') {
        await create.mutateAsync({ formData, userId: currentUser?.uid || '' });
        showToast('Role created successfully', 'success');
      } else if (selectedRole) {
        await update.mutateAsync({ roleId: selectedRole.id!, formData });
        showToast('Role updated successfully', 'success');
      }

      setDrawerOpen(false);
      resetForm();
      resetToFirstPage();
    } catch (err: any) {
      console.error('Error saving role:', err);
      showToast(`Failed to save role: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Initial full-page loading ─────────────────────────────────────────────
  if (rolesLoading && roles.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading roles...
        </Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <RoleIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              You don't have permission to view roles. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <RoleIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}
              >
                Role Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Define roles and assign feature permissions
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button
              onClick={handleAddRole}
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Create Role
            </Button>
          )}
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Total Roles
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}
                >
                  {totalRolesCount}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Roles defined in system
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Total Features
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}
                >
                  {APP_FEATURES.length}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    Across {FEATURE_GROUPS.length} groups
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Feature Groups
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}
                >
                  {FEATURE_GROUPS.length}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {FEATURE_GROUPS.join(', ')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  Avg Permissions
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}
                >
                  {totalRolesCount > 0 && roles.length > 0
                    ? Math.round(
                      roles.reduce((sum, r) => sum + r.permissions.length, 0) / roles.length
                    )
                    : '—'}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    permissions per role
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search roles… (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button
                  onClick={clearFilters}
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon />}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                <Typography variant="body2" color="text.secondary">
                  {hasActiveFilters
                    ? `Found ${roles.length} roles matching your search`
                    : `Showing ${roles.length} roles on this page`}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {roles.length === 0 && !loading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <RoleIcon
                  sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Roles Match Your Search' : 'No Roles Found'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}
                >
                  {hasActiveFilters
                    ? 'Try adjusting your search criteria.'
                    : "You haven't created any roles yet. Start by creating your first role."}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Search
                  </Button>
                ) : (
                  canCreate && (
                    <Button onClick={handleAddRole} variant="contained" startIcon={<AddIcon />}>
                      Create Role
                    </Button>
                  )
                )}
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: { xs: 'auto', md: 0 },
            }}
          >
            <TableContainer
              component={Paper}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 480, md: 900 } },
              }}
            >
              {loading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
                  <LinearProgress />
                </Box>
              )}

              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Role Name</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Description
                    </TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Updated
                    </TableCell>
                    {(canEdit || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow
                      key={role.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                      }}
                    >
                      {/* Role name */}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1,
                              bgcolor: isDark ? 'primary.dark' : 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <RoleIcon
                              sx={{
                                fontSize: 20,
                                color: isDark ? 'primary.light' : 'primary.dark',
                              }}
                            />
                          </Box>
                          <Box minWidth={0}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              }}
                              noWrap
                            >
                              {role.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: { xs: 'block', sm: 'none' } }}
                              noWrap
                            >
                              {role.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Description */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 260,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {role.description || '—'}
                        </Typography>
                      </TableCell>

                      {/* Permissions summary */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          <Chip
                            label={`${role.permissions.length} permissions`}
                            size="small"
                            variant="outlined"
                            icon={<LockIcon />}
                            sx={getSoftChipSx(
                              role.permissions.length === APP_FEATURES.length
                                ? 'success'
                                : role.permissions.length > 0
                                  ? 'info'
                                  : 'default',
                              isDark
                            )}
                          />
                          {/* Show up to 2 group badges */}
                          {FEATURE_GROUPS.filter((g) =>
                            APP_FEATURES
                              .filter((f) => f.group === g)
                              .some((f) => role.permissions.includes(f.code))
                          )
                            .slice(0, 2)
                            .map((g) => (
                              <Chip
                                key={g}
                                label={g}
                                size="small"
                                variant="outlined"
                                sx={{
                                  ...getSoftChipSx('secondary', isDark),
                                  display: { xs: 'none', md: 'inline-flex' },
                                }}
                              />
                            ))}
                        </Stack>
                      </TableCell>

                      {/* Updated */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {role.updatedAt?.toLocaleDateString() ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      {(canEdit || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            <Tooltip title="View Details">
                              <IconButton onClick={() => handleViewRole(role)} size="small" color="info">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditRole(role)} size="small" color="primary">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteRole(role)}
                                  disabled={deleteLoading === role.id}
                                  size="small"
                                  color="error"
                                >
                                  {deleteLoading === role.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Showing{' '}
                    {totalRoles === 0 ? 0 : (currentPage - 1) * ROLES_PER_PAGE + 1}–
                    {Math.min(currentPage * ROLES_PER_PAGE, totalRoles)} of {totalRoles} roles
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Add / Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer header */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <RoleIcon color="primary" />
              <Typography variant="h6">
                {drawerMode === 'add' ? 'Create New Role' : 'Edit Role'}
              </Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer scrollable body */}
          <Box
            ref={formScrollRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 3,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}
          >
            {/* ── Role name ── */}
            <TextField
              fullWidth
              label="Role Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              placeholder="e.g. Content Editor"
            />

            {/* ── Description ── */}
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
              required
              multiline
              rows={3}
              placeholder="Describe what this role can do…"
            />

            {/* ── Permissions ── */}
            <Box>
              {/* Permissions header with select-all */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Permissions
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.permissions.length} of {APP_FEATURES.length} selected
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      size="small"
                      color="primary"
                      checkedIcon={<CheckBoxIcon />}
                      indeterminateIcon={<IndeterminateCheckBoxIcon />}
                      icon={<CheckBoxOutlineBlankIcon />}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Stack>

              {formData.permissions.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  At least one permission must be selected before saving.
                </Alert>
              )}

              {/* Permission groups */}
              <Stack spacing={1.5}>
                {FEATURE_GROUPS.map((group) => {
                  const groupFeatures = APP_FEATURES.filter((f) => f.group === group);
                  return (
                    <PermissionGroup
                      key={group}
                      group={group}
                      features={groupFeatures}
                      selectedCodes={formData.permissions}
                      onChange={handlePermissionChange}
                      onSelectAll={handleSelectAllGroup}
                      isDark={isDark}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>

          {/* Drawer footer */}
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleCloseDrawer}
              disabled={saveLoading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={saveLoading}
              startIcon={
                saveLoading ? <CircularProgress size={16} /> : <SaveIcon />
              }
              sx={{ display: (drawerMode === 'add' ? canCreate : canEdit) ? undefined : 'none' }}
            >
              {drawerMode === 'add' ? 'Create Role' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── View Role Dialog ── */}
      <ViewRoleDialog
        open={viewDialogOpen}
        role={viewRole}
        onClose={() => setViewDialogOpen(false)}
        isDark={isDark}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={
              deleteDialogLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen operation overlay ── */}
      <Backdrop
        open={saveLoading || deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {deleteDialogLoading ? 'Deleting Role…' : saveAction}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default RoleManagement;